/**
 * Validation Error Dialog
 *
 * Displays validation errors when user attempts to save an invalid configuration.
 * Matches ect2-web behavior with expandable error categories.
 */
import React from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
} from "@mui/material";
import {
	ExpandMore as ExpandMoreIcon,
	Error as ErrorIcon,
	Warning as WarningIcon,
} from "@mui/icons-material";
import {
	formatValidationErrors,
	getErrorTitle,
	getErrorMessage,
} from "../utils/configValidation";

const ValidationErrorDialog = ({ open, onClose, errors = [] }) => {
	if (!errors || errors.length === 0) {
		return null;
	}

	const groupedErrors = formatValidationErrors(errors);
	const errorTypes = Object.keys(groupedErrors);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			aria-labelledby="validation-error-dialog-title"
		>
			<DialogTitle id="validation-error-dialog-title">
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<ErrorIcon color="error" />
					<Typography variant="h6">Configuration Errors</Typography>
				</Box>
			</DialogTitle>
			<DialogContent dividers>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Please fix the following errors before saving:
				</Typography>
				{errorTypes.map((errorType) => (
					<Accordion key={errorType} defaultExpanded>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							aria-controls={`${errorType}-content`}
							id={`${errorType}-header`}
						>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<WarningIcon color="warning" fontSize="small" />
								<Typography fontWeight="medium">
									{getErrorTitle(errorType)}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									({groupedErrors[errorType].length} issue
									{groupedErrors[errorType].length > 1 ? "s" : ""})
								</Typography>
							</Box>
						</AccordionSummary>
						<AccordionDetails>
							<Typography variant="body2" sx={{ mb: 1 }}>
								{getErrorMessage(errorType, groupedErrors[errorType])}
							</Typography>
							<List dense>
								{groupedErrors[errorType].map((error, index) => (
									<ListItem key={index} sx={{ py: 0.5 }}>
										<ListItemIcon sx={{ minWidth: 32 }}>
											<ErrorIcon color="error" fontSize="small" />
										</ListItemIcon>
										<ListItemText
											primary={error.layoutName}
											secondary={error.message}
										/>
									</ListItem>
								))}
							</List>
						</AccordionDetails>
					</Accordion>
				))}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} variant="contained" autoFocus>
					OK
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ValidationErrorDialog;
